'use client';

import { useEffect, useState } from 'react';
import { fetchLocalizationsBySlug } from '@/api/localizations';
import { API_TYPES, pluralMap } from './const';
import type { LocaleLanguage } from '@/lib/locale-context';

export type AlternateLocaleTarget = {
  /** Slug of the same content in the other locale, empty when unknown. */
  alternateSlug: string;
  /** Collection segment the URL needs, `pages` when the path carries none. */
  apiType: string;
};

const EMPTY: AlternateLocaleTarget = { alternateSlug: '', apiType: 'pages' };

/**
 * One lookup per path, shared by every switcher on the page.
 *
 * The header renders a switcher for desktop and one inside the mobile menu,
 * and both are mounted with the page now that the panels ship server-rendered.
 * Without this cache that is two identical Strapi round trips on every view.
 */
const cache = new Map<string, AlternateLocaleTarget>();
const inFlight = new Map<string, Promise<AlternateLocaleTarget>>();

/** Test seam: the cache outlives a render, so suites must start from empty. */
export const clearAlternateLocaleCache = () => {
  cache.clear();
  inFlight.clear();
};

const resolveTarget = async (
  pathname: string,
  locale: LocaleLanguage,
): Promise<AlternateLocaleTarget> => {
  const segments = pathname.split('/').filter(Boolean);
  const detectedApiType = segments.find((segment) =>
    API_TYPES.includes(segment),
  );

  // smzHub is a single route in both locales, so its own segment is the slug.
  if (segments.includes('smzhub')) {
    return { alternateSlug: 'smzhub', apiType: '' };
  }

  const apiType = detectedApiType || 'pages';

  try {
    const localizations = await fetchLocalizationsBySlug({
      collection: pluralMap[apiType],
      slug: segments[segments.length - 1],
      locale,
    });

    return { alternateSlug: localizations[0]?.slug || '', apiType };
  } catch (error) {
    console.error('Failed to fetch localization', error);
    return { alternateSlug: '', apiType };
  }
};

export const useAlternateLocaleSlug = (
  currentLocale: LocaleLanguage,
  pathname: string | null,
): AlternateLocaleTarget => {
  const key = `${currentLocale}:${pathname ?? ''}`;
  const [target, setTarget] = useState<AlternateLocaleTarget>(
    () => cache.get(key) ?? EMPTY,
  );

  useEffect(() => {
    if (!pathname) return;

    const cached = cache.get(key);
    if (cached) {
      setTarget(cached);
      return;
    }

    let active = true;
    const pending =
      inFlight.get(key) ??
      resolveTarget(pathname, currentLocale).then((resolved) => {
        cache.set(key, resolved);
        inFlight.delete(key);
        return resolved;
      });

    inFlight.set(key, pending);
    pending.then((resolved) => {
      if (active) setTarget(resolved);
    });

    return () => {
      active = false;
    };
  }, [currentLocale, key, pathname]);

  return target;
};
